import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export interface BusinessTaxSettings {
  id: string
  tenant_id: string

  vat_rate: number
  vat_enabled: boolean

  cit_rate_small: number
  cit_rate_medium: number
  cit_rate_large: number
  cit_turnover_small_threshold: number
  cit_turnover_medium_threshold: number

  tet_rate_small: number
  tet_rate_large: number

  minimum_tax_rate: number

  emtl_rate: number

  stamp_duty_rate: number
  stamp_duty_threshold: number

  company_tin: string
  paye_tin: string
  nsitf_number: string
  itf_number: string
  pfa_code: string

  created_at: string
  updated_at: string
  updated_by: string | null
}

export interface BusinessTaxSettingsHistory {
  id: string
  tenant_id: string
  field_name: string
  previous_value: string | null
  new_value: string | null
  note: string
  changed_by: string | null
  changed_at: string
}

export type EditableBusinessField = Exclude<
  keyof BusinessTaxSettings,
  'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'updated_by'
>

type FieldKind = 'currency' | 'percent' | 'boolean' | 'text'
type FieldGroup = 'vat' | 'cit' | 'tet' | 'minimum_tax' | 'emtl' | 'stamp_duty' | 'tin'

export const BUSINESS_FIELD_META: Record<EditableBusinessField, { label: string; kind: FieldKind; group: FieldGroup }> = {
  vat_rate:    { label: 'VAT Rate (%)',    kind: 'percent', group: 'vat' },
  vat_enabled: { label: 'VAT Enabled',     kind: 'boolean', group: 'vat' },

  cit_rate_small:                { label: 'CIT Rate – Small Co. (%)',     kind: 'percent',  group: 'cit' },
  cit_rate_medium:               { label: 'CIT Rate – Medium Co. (%)',    kind: 'percent',  group: 'cit' },
  cit_rate_large:                { label: 'CIT Rate – Large Co. (%)',     kind: 'percent',  group: 'cit' },
  cit_turnover_small_threshold:  { label: 'Small Co. Turnover Ceiling (₦)',  kind: 'currency', group: 'cit' },
  cit_turnover_medium_threshold: { label: 'Medium Co. Turnover Ceiling (₦)', kind: 'currency', group: 'cit' },

  tet_rate_small: { label: 'TET Rate – Small Co. (%)', kind: 'percent', group: 'tet' },
  tet_rate_large: { label: 'TET Rate – Large Co. (%)', kind: 'percent', group: 'tet' },

  minimum_tax_rate: { label: 'Minimum Tax Rate (%)', kind: 'percent', group: 'minimum_tax' },

  emtl_rate: { label: 'EMTL Rate (%)', kind: 'percent', group: 'emtl' },

  stamp_duty_rate:      { label: 'Stamp Duty Rate (%)',      kind: 'percent',  group: 'stamp_duty' },
  stamp_duty_threshold: { label: 'Stamp Duty Threshold (₦)', kind: 'currency', group: 'stamp_duty' },

  company_tin:  { label: 'Company TIN',          kind: 'text', group: 'tin' },
  paye_tin:     { label: 'PAYE TIN',             kind: 'text', group: 'tin' },
  nsitf_number: { label: 'NSITF Number',         kind: 'text', group: 'tin' },
  itf_number:   { label: 'ITF Number',           kind: 'text', group: 'tin' },
  pfa_code:     { label: 'PFA Code',             kind: 'text', group: 'tin' },
}

function metaFor(field: string): { label: string; kind: FieldKind; group: FieldGroup } | undefined {
  return (BUSINESS_FIELD_META as Record<string, { label: string; kind: FieldKind; group: FieldGroup }>)[field]
}

export function businessFieldLabel(field: string): string {
  return metaFor(field)?.label ?? field
}

export function formatBusinessFieldValue(field: string, raw: number | boolean | string | null | undefined): string {
  const meta = metaFor(field)
  if (raw === null || raw === undefined) return '—'
  if (!meta) return String(raw)

  if (meta.kind === 'boolean') {
    const b = typeof raw === 'boolean' ? raw : String(raw).toLowerCase() === 'true'
    return b ? 'ON' : 'OFF'
  }
  if (meta.kind === 'text') {
    const s = String(raw)
    return s.length === 0 ? '—' : s
  }
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw))
  if (!Number.isFinite(n)) return '—'

  if (meta.kind === 'percent')  return `${n.toFixed(2)}%`
  if (meta.kind === 'currency') return '₦' + n.toLocaleString('en-NG', { maximumFractionDigits: 2 })
  return String(raw)
}

export function useBusinessTaxSettings(tenantId: string) {
  return useQuery<BusinessTaxSettings | null>({
    queryKey: ['business-tax-settings', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('business_tax_settings')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as BusinessTaxSettings | null
    },
  })
}

export function useBusinessTaxSettingsHistory(tenantId: string, enabled: boolean) {
  return useQuery<BusinessTaxSettingsHistory[]>({
    queryKey: ['business-tax-settings-history', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await db('business_tax_settings_history')
        .select('id, tenant_id, field_name, previous_value, new_value, note, changed_by, changed_at')
        .eq('tenant_id', tenantId)
        .order('changed_at', { ascending: false })
        .limit(200)
      if (error) throw error
      return (data ?? []) as BusinessTaxSettingsHistory[]
    },
  })
}
